/* A question's comments, for both apps (backend services/questions.py): one thread, oldest first,
   with the answer marked. Each top-level comment can be replied to, one level deep: a reply sits
   under its comment (reply_to_id). Posting can answer the question or reopen an answered one; the
   project manager and administrators can mark an earlier comment as the answer. Edit and delete
   go through the project's comments, like any other comment: deleting a comment takes the replies
   under it, and the discussion's first comment takes the whole discussion. A cancelled question
   takes no new comments (backend services/questions.py): reopening it opens the discussion
   again. */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Api } from "../api/client";
import { Button, Check, ErrorNote, Loading } from "./basics";
import {
  Comment,
  Composer,
  deleteWithReplies,
  groupReplies,
  type Actions,
  type CommentGroup,
  type CommentItem,
  type CommentPerson,
} from "./Comments";
import { useLiveReload } from "./live";

const OPEN = ["internal", "client"];
const ANSWERED = ["answered_internal", "answered_client"];
const MAIN_COMPOSER = "main";

/** The Reply button under a comment, and the box it opens. */
function ReplyBox({
  people,
  busy,
  flagLabel,
  onDraftChange,
  onSubmit,
}: {
  people: CommentPerson[];
  busy: boolean;
  /** "This answers the question" or "Reopen the question", when either applies. */
  flagLabel: string | null;
  onDraftChange: (drafting: boolean) => void;
  onSubmit: (body: string, flag: boolean, report: (e: unknown) => void) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [flag, setFlag] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const close = () => {
    setOpen(false);
    setFlag(false);
    setError(null);
  };
  if (!open) {
    return (
      <div className="cm-foot">
        <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
          Reply
        </Button>
      </div>
    );
  }
  return (
    <div className="cm-replies">
      <Composer
        people={people}
        placeholder="Write a reply… (@ to mention)"
        submitLabel="Reply"
        busy={busy}
        error={error}
        autoFocus
        onDraftChange={onDraftChange}
        onCancel={close}
        extra={
          flagLabel && (
            <Check checked={flag} onChange={setFlag}>
              {flagLabel}
            </Check>
          )
        }
        onSubmit={async (body) => {
          const ok = await onSubmit(body, flag, setError);
          if (ok) close();
          return ok;
        }}
      />
    </div>
  );
}

export function QuestionComments({
  api,
  questionId,
  projectId,
  status,
  canMarkAnswer = false,
  onChanged,
  onDraftChange,
}: {
  api: Api;
  questionId: string;
  projectId: string;
  /** The question's status: whether a comment can answer it, or reopen it. */
  status: string;
  /** The project manager and administrators: Mark as answer on earlier comments. */
  canMarkAnswer?: boolean;
  /** The question changed (answered, reopened, new comment): refetch it. */
  onChanged?: () => void;
  /** A comment is typed and not yet posted (or no longer is). */
  onDraftChange?: (drafting: boolean) => void;
}) {
  const [comments, setComments] = useState<CommentItem[] | null>(null);
  const [people, setPeople] = useState<CommentPerson[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [flag, setFlag] = useState(false);
  const base = `/projects/${projectId}/comments`;
  const changedRef = useCallback(() => onChanged?.(), [onChanged]);

  // Which boxes hold typed text: the main one and any number of reply boxes. Each says so for
  // itself (and, closing, that it no longer does), so one closing never clears another's draft.
  const drafts = useRef(new Set<string>());
  const draftRef = useRef(onDraftChange);
  draftRef.current = onDraftChange;
  const setDraft = useCallback((box: string, drafting: boolean) => {
    if (drafting) drafts.current.add(box);
    else drafts.current.delete(box);
    draftRef.current?.(drafts.current.size > 0);
  }, []);

  const load = useCallback(async () => {
    try {
      const found = await api.get<{ comments: CommentItem[] }>(`/questions/${questionId}/comments`);
      setComments(found.comments);
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    }
  }, [api, questionId]);

  useEffect(() => {
    void load();
    api
      .get<CommentPerson[]>(`${base}/people`)
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [api, base, load]);
  useLiveReload(load); // someone else commented (UX-187)

  /** ``report`` is where a failure shows: a reply box shows its own. */
  const run = async (fn: () => Promise<unknown>, report: (e: unknown) => void = setError) => {
    setBusy(true);
    setError(null);
    report(null);
    try {
      await fn();
      await load();
      changedRef();
      return true;
    } catch (e) {
      report(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loadError && !comments) return <ErrorNote error={loadError} />;
  if (!comments) return <Loading />;

  const a: Actions = {
    people,
    canSetVisibility: false,
    taskShared: true,
    inTask: true,
    run,
    busy,
    error,
    api,
    base,
  };
  const canAnswer = OPEN.includes(status);
  const canReopen = ANSWERED.includes(status);
  const flagLabel = canAnswer ? "This answers the question" : "Reopen the question";
  // Only an open or answered question can be given an answer: not a cancelled or deferred one.
  const markable = canMarkAnswer && (canAnswer || canReopen);
  const cancelled = status === "cancelled";

  const tools = (c: CommentItem): ReactNode =>
    markable &&
    !c.is_answer && (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => void run(() => api.post(`/questions/${questionId}/answer`, { comment_id: c.id }))}
      >
        Mark as answer
      </Button>
    );
  // The first comment holds the thread together, so everything goes with it.
  const deleteText = ({ comment, replies }: CommentGroup) => {
    const others = comments.length - 1;
    if (comment.id !== comment.thread_id || others === 0) return deleteWithReplies(replies.length);
    return others === 1
      ? "This comment starts the discussion: the other comment goes with it. Delete both?"
      : `This comment starts the discussion: the other ${others} comments go with it. Delete them all?`;
  };
  const post = (body: string, chosen: boolean, replyTo?: string) =>
    api.post(`/questions/${questionId}/comments`, {
      body,
      answered: canAnswer && chosen,
      reopen: canReopen && chosen,
      ...(replyTo ? { reply_to_id: replyTo } : {}),
    });

  return (
    <div className="cm-board" data-live-safe="">
      {comments.length === 0 ? (
        <p className="muted">No comments yet.</p>
      ) : (
        <div className="cm-thread" aria-label="Comments on the question">
          {groupReplies(comments).map((g) => (
            <div className="cm-group" key={g.comment.id}>
              <Comment comment={g.comment} a={a} tools={tools(g.comment)} deleteText={deleteText(g)} />
              {g.replies.length > 0 && (
                <div className="cm-replies">
                  {g.replies.map((r) => (
                    <Comment key={r.id} comment={r} a={a} tools={tools(r)} />
                  ))}
                </div>
              )}
              {!cancelled && (
                <ReplyBox
                  people={people}
                  busy={busy}
                  flagLabel={canAnswer || canReopen ? flagLabel : null}
                  onDraftChange={(drafting) => setDraft(g.comment.id, drafting)}
                  onSubmit={(body, chosen, report) => run(() => post(body, chosen, g.comment.id), report)}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {cancelled ? (
        <>
          {!!error && <ErrorNote error={error} />}
          <p className="muted">Reopen the question to discuss it.</p>
        </>
      ) : (
        <Composer
          people={people}
          placeholder="Comment on the question… (@ to mention)"
          submitLabel="Comment"
          busy={busy}
          error={error}
          onDraftChange={(drafting) => setDraft(MAIN_COMPOSER, drafting)}
          extra={
            (canAnswer || canReopen) && (
              <Check checked={flag} onChange={setFlag}>
                {flagLabel}
              </Check>
            )
          }
          onSubmit={async (body) => {
            const ok = await run(() => post(body, flag));
            if (ok) setFlag(false);
            return ok;
          }}
        />
      )}
    </div>
  );
}
